/**************************************************************************************************
 *
 *  This is the main container for all location related views: Location Detail (hierarch), Location
 *  Edit/Creation, Build Edit/Creation, Task Detail, Field Task Detail
 *
 *  This views are individual components that will fit into the detail-container parent element
 *  defined in this container.
 *
 **************************************************************************************************/

import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import BaseComponent from '../../../baseClasses/BaseComponent';
import animator from '../../../helpers/animator';
import LocationAddCard from './LocationAddCard';
import PORLocationParentCard from './PORLocationParentCard';
import PORProjectParentCard from './PORProjectParentCard';
import ProjectSearchFilter from '../../../components/ProjectsNav/ProjectSearchFilter';
import LoadingDots from '../../../components/LoadingDots';
import CollectionDataSource from '../../../dao/DataSource/CollectionDataSource';
import locationInactiveIcon from '../../../assets/images/map-inactive.svg';
import locationActiveIcon from '../../../assets/images/map-active.svg';
import projectInactiveIcon from '../../../assets/images/por-project-nav-icon.svg';
import projectActiveIcon from '../../../assets/images/por-project-active.svg';

export const projects_nav_root = {
  project: {
    location: {},
    project_contacts: { user: {} },
    users: {},
  },
};

import AddIcon from '../../../assets/images/add-icon-white.svg';
import './PORNav.scss';

export class PORNav extends BaseComponent {
  constructor(props) {
    super(props);
    this.state = {
      searchString: '',
      showAddButton: true,
      selectedTab: 'location-tab',
      loadingMore: true,
    };
    this.animator = new animator();
    this.initializing = true;

    // TODO: proptypes
    this.editEnabled = true;
    this.baseRoute = '/por';

    this.createLocationCards = this.createLocationCards.bind(this);
  }

  isLocationFirstPorPath() {
    const path = this.props.location.pathname;
    return path.indexOf('/por/location/') === 0 && path.indexOf('/edit') === -1;
  }

  isProjectFirstPorPath() {
    return this.props.location.pathname.indexOf('/por/project/') === 0;
  }

  handleInitializeDataSources(source) {
    if (this.state.selectedTab == 'location-tab') {

      if (!source) {
        if (this.props.viewName == 'locationCRUD' && this.state.showAddButton) {
          this.setState({ showAddButton: false });
        }
        else if (!this.state.showAddButton) {
          this.setState({ showAddButton: true });
        }
      }
      // Sync selection from URL on initial load (!source) or when switching to this tab while already
      // on a location-first route (e.g. navigated from project-first POR). Passing the tab name as
      // `source` skips `!source`, so without this branch IDs never match the URL after that transition.
      if (!source || (source === 'location-tab' && this.isLocationFirstPorPath())) {
        const project_id = this.props.params.project_id;
        const location_id = this.props.params.location_id;
        if (location_id || project_id) {
          this.setState({ selectedProjectId: project_id, selectedLocationId: location_id });
        }
      }
      if (!this.locationsDataSource) {
        const perPage = Math.floor((window.innerHeight - 110) / 63) + 1;
        this.locationsDataSource = new CollectionDataSource('LocationsNavCollection', 'Location', { locations: {} }, this.handleLocationsRetrieval,
          'LocationsNav',
          perPage, null, this.props.currentUser.id, this.props.currentCompany.id);
        this.locationsDataSource.perPage = perPage;
        
      }
      this.locationsDataSource.current(source);
    }
    else {
      if (!source || (source === 'project-tab' && this.isProjectFirstPorPath())) {
        const project_id = this.props.params.project_id;
        const location_id = this.props.params.location_id;
        if (project_id || location_id) {
          this.setState({ selectedProjectId: project_id, selectedLocationId: location_id });
        }
      }
      if (!this.projectsDataSource) {
        // Page size must be a whole number. Kaminari truncates `per` (per('16.31') -> LIMIT 16), so a
        // fractional perPage means a page can never return as many rows as it asked for, and
        // ProjectsCollection#fetchNext (nextPageForLoadedItems: floor(loaded / perPage) + 1) then
        // returns the page it just fetched - the projects tab re-requests page 1 on every scroll,
        // the merge dedupes to nothing, and the list never grows.
        const perPage = Math.floor((window.innerHeight - 110) / 63) + 1;
        this.projectsDataSource = new CollectionDataSource('ProjectsCollection', 'Project', projects_nav_root, this.handleProjectsRetrieval,
          'por_projects_nav', null, null, null,
          this.props.currentCompany.id, 'projects', null, projects_nav_root, true, null, true);
        this.projectsDataSource.searchString = '';
        this.projectsDataSource.filter = {};
        this.projectsDataSource.perPage = perPage;
      }
      this.projectsDataSource.current(source);
    }
  }

  componentDidMount() {
    if (this.props.location.pathname.indexOf('/por/project') == 0) {
      // Set selectedProjectId from URL params to prevent auto-redirect to first project
      const project_id = this.props.params.project_id;
      // Use class property for immediate access (state updates are async)
      this._initialProjectId = project_id;
      this.setState({ 
        selectedTab: 'project-tab', 
        showAddButton: false,
        selectedProjectId: project_id,
        selectedLocationId: this.props.params.location_id,
      });
    }
    else {
      this.handleInitializeDataSources();
    }
  }

  handleLocationsRetrieval(navigationLocations, action, acData, fetchedData) {
    this.setState({ navigationLocations });
    if (action != 'prefetch') {
      this.setState({ loadingMore: false });
      this.scrollinProgress = false;
    }
    if (action == 'location-tab') {
      const urlLocationId = this.props.params.location_id;
      const urlProjectId = this.props.params.project_id;
      if (this.isLocationFirstPorPath() && urlLocationId && urlLocationId !== 'all') {
        const locations = navigationLocations.locations || [];
        const match = locations.find(l => String(l.id) === String(urlLocationId));
        if (match) {
          this.handleSelectParentLocation(match);
          return;
        }
        this.setState({
          selectedLocationId: urlLocationId,
          selectedProjectId: urlProjectId != null ? urlProjectId : 'all',
        });
        return;
      }
      this.setState({ selectedLocationId: null, selectedProjectId: null });
      this.handleSelectParentLocation(navigationLocations.meta && navigationLocations.meta.selected_location ? navigationLocations.meta.selected_location :
        navigationLocations.locations.length > 0 ? navigationLocations.locations[0]: null);
    }
    else if (acData) {
      if (acData.type == 'deleteLocation' && acData.updating_user_id == this.props.currentUser.id) {
        this.setState({ selectedLocationId: null, selectedProjectId: null });
        this.handleSelectParentLocation(navigationLocations.meta && navigationLocations.meta.selected_location ? navigationLocations.meta.selected_location :
          navigationLocations.locations.length > 0 ? navigationLocations.locations[0]: null);
      }
      else if (acData.type == 'newLocation' && acData.updating_user_id == this.props.currentUser.id) {
        const addedLocation = navigationLocations.locations.find(location => { return location.id == acData.location_id; });
        if (addedLocation) {
          this.handleEditLocation(addedLocation);
        }
      }
    }
    else if (!this.state.selectedLocationId) {
      this.handleSelectParentLocation(navigationLocations.meta && navigationLocations.meta.selected_location ? navigationLocations.meta.selected_location :
        navigationLocations.locations.length > 0 ? navigationLocations.locations[0]: null);
    }
  }

  componentWillUnmount() {
    if (this.locationsDataSource) {
      this.locationsDataSource.release();
      this.locationsDataSource = null;
    }
    if (this.projectsDataSource) {
      this.projectsDataSource.release();
      this.projectsDataSource = null;
    }
  }
  
  skipAutoSelectFirstProject() {
    const search = (this.props.location && this.props.location.search) || '';
    const navigationParam =  new URLSearchParams(search).get('navigation');
    return navigationParam === 'notification' || navigationParam === 'skip_select';
  }

  handleProjectsRetrieval(navigationProjects, action, acData, fetchedData) {
    this.setState({ navigationProjects });
    if (action != 'prefetch'){
      this.setState({ loadingMore: false });
      this.scrollinProgress = false;
    }
    // When navigated from notification (?navigation=notification), never auto-select first project.
    const skipAutoSelect = this.skipAutoSelectFirstProject();
    if (action == 'project-tab' && !skipAutoSelect) {
      const urlProjectId = this.props.params.project_id;
      const urlLocationId = this.props.params.location_id;
      if (this.isProjectFirstPorPath() && urlProjectId && urlProjectId !== 'all') {
        const projects = navigationProjects.projects || [];
        const match = projects.find(p => String(p.id) === String(urlProjectId));
        if (match) {
          this.handleSelectParentProject(match);
          return;
        }
        this.setState({
          selectedProjectId: urlProjectId,
          selectedLocationId: urlLocationId != null ? urlLocationId : 'all',
        });
        return;
      }
      this.setState({ selectedLocationId: null, selectedProjectId: null });
      const firstProject = navigationProjects.projects && navigationProjects.projects.length > 0 ? navigationProjects.projects[0] : null;
      if (firstProject) {
        this.handleSelectParentProject(firstProject);
      }
    }
    // Only auto-select first project if no project is already selected (e.g. initial load on project URL)
    else if (!skipAutoSelect && !this.state.selectedProjectId && !this._initialProjectId) {
      this.handleSelectParentProject(navigationProjects.projects[0]);
    }
    // Clear the class property after first use
    this._initialProjectId = null;
  }

  componentDidUpdate(prevProps, prevState) {
    const pathname = this.props.location.pathname;
    const prevPathname = prevProps.location.pathname;
    if (pathname !== prevPathname) {
      const onProjectNavPath = pathname.indexOf('/por/project') === 0;
      if (onProjectNavPath && this.state.selectedTab !== 'project-tab') {
        this.setState({
          selectedTab: 'project-tab',
          showAddButton: false,
          selectedProjectId: this.props.params.project_id,
          selectedLocationId: this.props.params.location_id,
        });
      }
      else if (!onProjectNavPath && this.state.selectedTab !== 'location-tab') {
        this.setState({
          selectedTab: 'location-tab',
          showAddButton: true,
          searchString: this.locationsDataSource ? this.locationsDataSource.searchString : '',
        });
      }
    }

    if (this.state.selectedTab != prevState.selectedTab) {
      this.handleInitializeDataSources(this.state.selectedTab);
    }
    else {
      const project_id = this.props.params.project_id;
      const location_id = this.props.params.location_id;
      if (!location_id && !project_id) {
        if (this.state.navigationLocations) {
          this.handleRepositionLocation();
        }
      }
      else{
        let selectedProjectId = this.state.selectedProjectId;
        let selectedLocationId = this.state.selectedLocationId;
        if (location_id != selectedLocationId || project_id != selectedProjectId) {
          this.setState({ selectedProjectId: project_id, selectedLocationId: location_id });
        }
        if (this.props.viewName == 'locationCRUD') {
          if (this.state.showAddButton) {
            this.setState({ showAddButton: false });
          }
        }
        else if (!this.state.showAddButton) {
          this.setState({ showAddButton: true });
        }
      }
    }
  }

  handleSelectChildProject(project, location) {
    if (project) {
      this.setState({ selectedProjectId: project.id, selectedLocationId: location.id });
      this.props.history.push(this.baseRoute + '/project/' + project.id + '/location/all?tab=documents');
    }
  }

  handleSelectChildLocation(location, project) {
    if (location) {
      this.setState({ selectedLocationId: location.id, selectedProjectId: project.id });
      this.props.history.push(this.baseRoute + '/project/' + project.id + '/location/' + location.id);
    }
  }

  handleSelectParentLocationFromCard(location) {
    if (location) {
      let location_id = location.id ? location.id : location;
      this.props.history.push(this.baseRoute + '/location/' + location_id + '/project/all');
    }
  }

  handleSelectParentLocation(location) {
    if (location) {
      let location_id = location.id ? location.id: location;
      let id = '';
      const closeout_id = this.props.params.closeout_id;
      const document_id = this.props.params.document_id;
      if (!closeout_id && !document_id){
        this.props.history.push(this.baseRoute + '/location/' + location_id + '/project/all');
      }
      id = 'location-' + location_id;
      this.setState({ selectedLocationId: location_id, selectedProjectId: 'all' });
      const element = document.getElementById(id);
      if (element) {
        const rect = element.getBoundingClientRect();
        let parentElement = element.parentElement;
        if (parentElement) {
          parentElement = parentElement.parentElement;
        }

        if (parentElement) {
          const parentRect = parentElement.getBoundingClientRect();
          if (rect.top < parentRect.top || rect.bottom > parentRect.bottom) {
            this.scrollIntoView(element);
          }
        }
      }
    }
    else {
      this.props.history.replace('/por/location');
    }
  }

  handleSelectParentProject(project) {
    if (project) {
      this.setState({ selectedLocationId: null });
      let project_id = project.id ? project.id: project;
      let id = '';
      const closeout_id = this.props.params.closeout_id;
      const document_id = this.props.params.document_id;
      if (!closeout_id && !document_id){
        this.props.history.push(this.baseRoute + '/project/' + project_id + '/location/all');
      }
      id = 'project-' + project_id;
      if (project) {
        this.setState({ selectedProjectId: project_id, selectedLocationId: 'all' });
        const element = document.getElementById(id);
        if (element) {
          const rect = element.getBoundingClientRect();
          let parentElement = element.parentElement;
          if (parentElement) {
            parentElement = parentElement.parentElement;
          }

          if (parentElement) {
            const parentRect = parentElement.getBoundingClientRect();
            if (rect.top < parentRect.top || rect.bottom > parentRect.bottom) {
              this.scrollIntoView(element);
            }
          }
        }
      }
    }
  }

  handleSelectParentProjectFromCard(project) {
    if (project) {
      this.setState({ selectedLocationId: null });
      let project_id = project.id ? project.id : project;
      this.props.history.push(this.baseRoute + '/project/' + project_id + '/location/all');
      this.setState({ selectedProjectId: project_id, selectedLocationId: 'all' });
    }
  }

  handleRepositionLocation() {
    if (!this.state.showAddButton) {
      this.setState({ showAddButton: true });
    }
  }

  handleEditLocation(location) {
    this.props.history.push('/por/location/' + location.id + '/edit');
    this.setState({ selectedLocationId: location.id });
    this.isEditing = true;
    const element = document.getElementById('location-' + location.id);
    if (element) {
      this.scrollIntoView(element);
    }
  }

  handleDisplayAddCard() {
    this.setState({ displayAddCard: true, showAddButton: false });
  }
  
  handleCloseAddCard() {
    this.setState({ displayAddCard: false, showAddButton: true });
  }
  
  handleLocationAdd(location) {
    this.scroller.scrollTop = 0;
    this.props.history.push('/por/location/' + location.id + '/edit');
  }
  
  handleLocationSearch(filter, searchString) {
    if (this.locationsDataSource) {
      this.locationsDataSource.searchString = searchString;
      this.locationsDataSource.current();
    }
  }

  handleProjectSearch(filter, searchString) {
    if (this.projectsDataSource) {
      this.projectsDataSource.searchString = searchString;
      this.projectsDataSource.current();
    }
  }

  async handleFilterProjects(filter) {
    if (this.projectsDataSource) {
      this.projectsDataSource.filter = filter;
      return this.projectsDataSource.current();
    }
  }

  handleLocationSearchChange(searchString) {
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
    }
    let { filter } = this.state;
    const self = this;
    this.searchTimer = setTimeout(() => {
      self.setState({ searchString });
      this.handleLocationSearch(filter, searchString);
    }, 400);
  }

  handleProjectSearchChange(searchString) {
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
    }
    let { filter } = this.state;
    const self = this;
    this.searchTimer = setTimeout(() => {
      self.setState({ searchString });
      this.handleProjectSearch(filter, searchString);
    }, 400);
  }

  handleProjectFilterChange(filter) {
    if (this.projectsDataSource) {
      this.handleFilterProjects(filter).then(() => {
        this.setState({ filter });
      });
    }
    else {
      this.setState({ filter });
    }
  }

  /** SCROLLING/PAGINATION FUNCTIONS */
  scrollIntoView(element) {
    var rect = element.getBoundingClientRect();
    var parentElement = element.parentElement;
    var parentRect = parentElement.getBoundingClientRect();
    var scrollRect = this.scroller.getBoundingClientRect();
    const id = this.scroller.id;
    if (rect.top <= scrollRect.top) {
      let amountAlreadyScrolled = Math.abs(scrollRect.top - parentRect.top);
      let additionalScroll = scrollRect.top - rect.top;
      let scrollAmount = amountAlreadyScrolled - additionalScroll;
      const ani = this.animator.createPropertyAnimation(id, 250, {
        verticalScroll: {
          startVal: amountAlreadyScrolled,
          endVal: scrollAmount,
        },
      });
      this.animator.addAnimation(ani);
      this.animator.processAnimationQueue();
    } else if (rect.bottom >= scrollRect.bottom) {
      let amountAlreadyScrolled = Math.abs(scrollRect.top - parentRect.top);
      let additionalScroll = rect.bottom - scrollRect.bottom;
      let scrollAmount = amountAlreadyScrolled + additionalScroll;
      const ani = this.animator.createPropertyAnimation(id, 250, {
        verticalScroll: {
          startVal: amountAlreadyScrolled,
          endVal: scrollAmount,
        },
      });
      this.animator.addAnimation(ani);
      this.animator.processAnimationQueue();
    }
  }

  handleScrolling() {
    if (this.state.selectedTab == 'location-tab') {
      const { navigationLocations } = this.state;
      const listRect = this.list.getBoundingClientRect();
      const scrollRect = this.scroller.getBoundingClientRect();
      if (navigationLocations.ids.length < navigationLocations.totalLocations &&
        listRect.bottom - 19 <= scrollRect.bottom && !this.scrollinProgress) {
        this.scrollinProgress = true;
        this.setState({ loadingMore: true });
        this.locationsDataSource.next();
      }
    }
    else {
      const { navigationProjects } = this.state;
      const listRect = this.list.getBoundingClientRect();
      const scrollRect = this.scroller.getBoundingClientRect();
      if (navigationProjects.ids.length < navigationProjects.totalProjects &&
        listRect.bottom - 19 <= scrollRect.bottom && !this.scrollinProgress) {
        this.scrollinProgress = true;
        this.setState({ loadingMore: true });
        this.projectsDataSource.next();
      }
    }
  }

  splitByDigitsAndNonDigits(str) {
    return str.match(/\d+|\D+/g);
  };

  compareSegments(a, b) {
    const segmentsA = this.splitByDigitsAndNonDigits(a.toLowerCase());
    const segmentsB = this.splitByDigitsAndNonDigits(b.toLowerCase());
    
    const len = Math.min(segmentsA.length, segmentsB.length);

    for (let i = 0; i < len; i++) {
      const segmentA = segmentsA[i];
      const segmentB = segmentsB[i];

      if (segmentA !== segmentB) {
        const numA = parseInt(segmentA, 10);
        const numB = parseInt(segmentB, 10);

        // compare numerically
        if (!isNaN(numA) && !isNaN(numB)) {
          return numA - numB;
        }

        // compare lexically
        return segmentA.localeCompare(segmentB);
      }
    }

    return segmentsA.length - segmentsB.length;
  };

  /** RENDER FUNCTIONS */
  createLocationCards() {
    const { navigationLocations, selectedLocationId, selectedProjectId } = this.state;
    let locationCards = [];
    if (navigationLocations && navigationLocations.locations && navigationLocations.locations.length) {
      let sortedLocation;
      sortedLocation = navigationLocations.locations.sort((a, b) => this.compareSegments(a.name, b.name));
      navigationLocations.locations = sortedLocation;
      
      locationCards = navigationLocations.locations.map(location => {
        const selected = selectedLocationId == location.id && (selectedProjectId == 'all' || !selectedProjectId);
        const expanded = selectedProjectId && selectedProjectId != 'all' && selectedLocationId == location.id;
        return (
          <PORLocationParentCard 
            id={'location-' + location.id} 
            key={'nav-location-' + location.id} 
            location={location}
            selectedLocationId={selectedLocationId}
            selectedProjectId={selectedProjectId}
            selectLocation={this.handleSelectParentLocationFromCard} 
            selectProject={this.handleSelectChildProject}
            editLocation={this.handleEditLocation}
            filter={this.state.filter}
            selected={selected} 
            expanded={expanded}
            editEnabled={this.editEnabled}
          />
        );
      });
    }
    
    return locationCards;
  }

  createProjectCards() {
    const { navigationProjects, selectedProjectId, selectedLocationId } = this.state;
    let projectCards = [];
    if (navigationProjects && navigationProjects.projects && navigationProjects.projects.length) {
      let sortedProjects;
      sortedProjects = navigationProjects.projects.sort((a, b) => this.compareSegments(a.name, b.name));
      navigationProjects.projects = sortedProjects;

      projectCards = navigationProjects.projects.map(project => {
        const selected = selectedProjectId == project.id && (selectedLocationId == 'all' || !selectedLocationId);
        const expanded = selectedLocationId && selectedLocationId != 'all' && selectedProjectId == project.id;
        return (
          <PORProjectParentCard 
            id={'project-' + project.id} 
            key={'nav-project-' + project.id} 
            project={project}
            selectedLocationId={selectedLocationId}
            selectedProjectId={selectedProjectId}
            selectLocation={this.handleSelectChildLocation} 
            selectProject={this.handleSelectParentProjectFromCard}
            editLocation={this.handleEditLocation} 
            selected={selected} 
            expanded={expanded}
            editEnabled={this.editEnabled}
            history={this.props.history}
          />
        );
      });
    }
    
    return projectCards;
  }

  handleSelectLocationTab() {
    this.setState({ selectedTab: 'location-tab', searchString: this.locationsDataSource ? this.locationsDataSource.searchString : '', showAddButton: true });
  }

  handleSelectProjectTab() {
    this.setState({
      selectedTab: 'project-tab',
      searchString: this.projectsDataSource ? this.projectsDataSource.searchString : '',
      showAddButton: false,
      selectedProjectId: null,
      selectedLocationId: null,
    });
  }

  render() {
    const { permissions } = this.props;
    const { navigationLocations, navigationProjects } = this.state;

    if (!navigationLocations && !navigationProjects) {
      return null;
    }

    let navCards = null;
    
    const inLocationTab = this.state.selectedTab == 'location-tab';
    if (inLocationTab) {
      navCards = this.createLocationCards();
    }
    else {
      navCards = this.createProjectCards();
    }

    this.initializing = false;

    let addButton;
    if (this.state.selectedTab == 'location-tab' && this.props.permissions.canCreateLocation && !this.state.displayAddCard && this.state.showAddButton && !this.props.currentCompany.admin_company) {
      addButton = <img className="add-button" src={AddIcon} alt="add"
          onClick={this.handleDisplayAddCard} />;
    }
    let locationAddCard = null;
    if (this.state.displayAddCard && (permissions.isSuperUser || permissions.canCreateLocation)) {
      locationAddCard = (
        <LocationAddCard 
          id="location-add-card" 
          closeAddCard={this.handleCloseAddCard} 
          handleLocationAdd={this.handleLocationAdd} 
        />
      );
      this.scrollToAddCard = true;
    }

    let locationsLabel = this.textMap.locations;
    let projectsLabel = this.textMap.projects;
    let searchFilter = 
      <ProjectSearchFilter 
        hideFilter={true}
        handleSearchChange={inLocationTab ? this.handleLocationSearchChange: this.handleProjectSearchChange} 
        handleFilterChange={this.handleProjectFilterChange} 
        fromPOR={true}
        selectedFilters={this.state.filter}
        search={this.state.searchString}
        resetSearchString={this.state.resetSearchString}
        overridePlaceHolder={inLocationTab ? this.textMap.searchLocation : this.textMap.searchProject}
      />;

    let loadingMore = null;
    if(this.state.loadingMore){
      loadingMore = <div className='location-nav-loading-indicator'><LoadingDots /></div>;
    }
    let noneFound = null;
    if ((this.state.searchString || this.state.filter) && navCards.length == 0) {
      noneFound = <div className="no-matches-indicator">No Results Found...</div>;
    }
    // The projects list is member-scoped by the API for standard users, so it can legitimately come
    // back empty. Without this the sidebar is silently blank with no explanation.
    else if (!inLocationTab && navCards.length == 0 && !this.state.loadingMore) {
      noneFound = <div className="no-matches-indicator">No projects assigned to you.</div>;
    }

    return (
      <div className={'por-nav'}>
        <div className='por-tabs'>
          <div className={'por-tab left-por-tab' + (this.state.selectedTab == 'location-tab' ? ' selected-por-tab' : '')}
            onClick={this.handleSelectLocationTab}>
            <img className='por-tab-image' alt='por' src={this.state.selectedTab == 'location-tab' ? locationActiveIcon : locationInactiveIcon}/>
          </div>
          <div className={'por-tab right-por-tab' + (this.state.selectedTab == 'project-tab' ? ' selected-por-tab' : '')}
            onClick={this.handleSelectProjectTab}>
            <img className='por-tab-image' alt='por' src={this.state.selectedTab == 'project-tab' ? projectActiveIcon : projectInactiveIcon}/>
          </div>
        </div>
        {searchFilter}
        <div className={'por'}>
          <div className="section-header">
            <div className="section-header-text">
              {this.state.selectedTab == 'location-tab' ? locationsLabel.toUpperCase() : projectsLabel.toUpperCase()}
              </div>
            {addButton}
          </div>
          <div
            id="por-scroller"
            className={'por-scroller'}
            ref={element => {
              this.scroller = element;
              if (this.scroller) {
                this.scroller.addEventListener('scroll', this.handleScrolling);
              }
            }}
          >
            <div
              className="por-list" 
              ref={element => {
                this.list = element ? element : this.list;
              }}
            >
              {locationAddCard}
              {navCards}
              {loadingMore}
              {noneFound}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => {

  return {
    dataChanged: state.main.dataChanged,
    permissions: state.new_users ? state.new_users.permissions : null,
    currentUser: state.new_users ? state.new_users.currentUser : null,
    currentCompany: state.new_companies ? state.new_companies.currentCompany : null,
  };
};

export default withRouter(connect(mapStateToProps)(PORNav));



// WEBPACK FOOTER //
// ./src/containers/PORContainer/PORNav/index.js